import { ICompetitionCalendarResults, IMatchMenu } from "@sky-it-livedata/livedata-sports-design-system";
import {
  getGlider,
} from '../../../utils/glider';
import { ErrorName, WidgetError } from '../../../utils/errors';
import { intcmpMap } from '../../../utils/intcmp';
import { isValidCompetitionCalendarAndResults } from './typeguards';
import { 
  CreateCompetitionCalendarAndResultsInputSchema,
  CompetitionCalendarAndResultsMatchRound as IMatchRound,
  CompetitionCalendarAndResultsMatchDay as IMatchDay,
  CompetitionCalendarAndResultsMatch as IMatch
} from 'appsync';
import { CompetitionCalendarAndResultsQuery } from "../types";

const fixCompetitionPhase = (matchRoundList: IMatchRound[]): IMatchRound[] =>
  matchRoundList.map((matchRound: IMatchRound) => {
    const {status, ...rest} = matchRound; // status is used only for glider menu, 
    return {
      ...rest,
      matchDayList:
        matchRound.matchDayList &&
        matchRound.matchDayList
          .filter(Boolean)
          .map((md) => {
            const matchDay = md as IMatchDay;
            return {
              ...matchDay,
              matchList: (matchDay.matchList || [])
                .filter(Boolean)
                .map((match) => ({
                  competitionPhase: matchRound.competitionPhase,
                  ...(match as IMatch),
                })),
            };
          }),
    };
  });

const transformer = (obj: CompetitionCalendarAndResultsQuery): ICompetitionCalendarResults => {
  if (isValidCompetitionCalendarAndResults(obj)) {
    CreateCompetitionCalendarAndResultsInputSchema().parse(obj.data?.competitionCalendarAndResults, {reportInput: true});

    const matchRoundList: IMatchRound[] = (obj.data?.competitionCalendarAndResults?.matchRoundList ?? []) as IMatchRound[];
    const fixedMatchRoundList: IMatchRound[] = fixCompetitionPhase(matchRoundList);
    const lineari: IMatchRound[] = fixedMatchRoundList.filter((round) => !round.competitionPhase);
    const nonLineari: IMatchRound[] = fixedMatchRoundList.filter((round) => round.competitionPhase);

    const groupedNonLineari: Record<string, IMatchRound[]> = nonLineari.reduce(
      (grouped, round: IMatchRound) => {
        const phase = round.competitionPhase;
        const key = phase == null ? 'null' : String(phase);
        grouped[key] = [...(grouped[key] || []), round];
        return grouped;
      },
      {} as Record<string, IMatchRound[]>
    );

    const mergedNonLineari: IMatchRound[] = Object.values(groupedNonLineari).map((arr) => {
      const [andata, ritorno] = arr;
      if (!ritorno) {
        return andata;
      }

      return {
        ...ritorno,
        matchDayList: [...(andata.matchDayList || []), ...(ritorno.matchDayList || [])],
      };
    });

    const competitionMatchListMenu: IMatchMenu = getGlider(obj.data?.competitionCalendarAndResults, null);

    // ensure every round has a numeric `round` property (fallback to 0) and a non-undefined matchDayList array
    const competitionMatchListTyped = [...lineari, ...mergedNonLineari].map((r) => ({
      ...r,
      round: typeof r.round === 'number' ? r.round : 0,
      matchDayList: (r.matchDayList || []) as any,
    })) as ICompetitionCalendarResults['competitionMatchList'];

    const transformedObject: ICompetitionCalendarResults = {
      competitionMatchList: competitionMatchListTyped,
      competitionMatchListMenu,
      trackingData: intcmpMap.competitionCalendarAndResults,
    };

    return transformedObject;
  } else {
    throw new WidgetError<ErrorName>({
      name: 'NO_VALID_DATA',
      message: 'Invalid transformer input, Error in CompetitionCalendarResultsQuery object',
      data: obj
    });
  }
};

export default transformer;
